import { Divider } from '@material-ui/core'
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined'
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined'
import { permissionsSelectors } from 'ducks/permissions'
import { setQuickStartAction } from 'ducks/quickStart'
import { viewModeSelectors } from 'ducks/viewMode'
import BasicInput from 'elements/input/BasicInput'
import UiContainer from 'elements/UiContainer'
import { ComponentVersions_3_0, ComponentVersionsInherit, IconButton, styled, Typography } from 'opensolar-ui'
import { useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import { connect, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toCapitalise } from 'util/misc'
import CategoryItem from './elements/CategoryItem'
import FoldArrows from './elements/FoldArrows'
import MenuItemLink from './elements/MenuItemLink'
import getDefaultExpanded from './hooks/getDefaultExpanded'
import { filterVisibleMenuData } from './menuFilter'
import { useMenuData } from './useMenuData'

// Bad, to add id in menu data config
const generateIdForMenuItem = (title) => {
  const parts = title.split(' ').map((str) => toCapitalise(str))
  return 'menuItem' + parts.join('')
}

const styles = {
  main: {
    display: 'flex',
    position: 'relative',
    padding: '10px 0',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: '100%',
    color: '#4d4d4d',
    boxShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 6px, rgba(0, 0, 0, 0.12) 0px 1px 4px',
    backgroundColor: '#ffffff',
    overflow: 'inherit',
  },
  title: {
    fontSize: 16,
    verticalAlign: 'middle',
    paddingLeft: 5,
  },
  quickStart: {
    wrapper: {
      color: '#ffffff',
      background: '#1890ff',
      borderRadius: '4px',
      padding: 5,
    },
  },
}

const SearchRow = styled('div', { name: 'ControlMenuSearchRow' })({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 10px 10px',
})

const SearchWrapper = styled('div', { name: 'ControlMenuSearchWrapper' })({
  flex: 1,
  minWidth: 0,
})

const SearchIcon = styled(SearchOutlinedIcon, { name: 'ControlMenuSearchIcon' })(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginRight: 6,
}))

const ClearSearchButton = styled(IconButton, { name: 'ControlMenuClearSearchButton' })({
  padding: 4,
})

const NoResults = styled('div', { name: 'ControlMenuNoResults' })({
  padding: '16px 14px',
})

const MenuList = ({
  category,
  onMenuTap,
  accessRightsSetting,
  forceOpen,
  expanded,
  onToggleExpanded,
  highlightText,
}) => {
  const isOpen = forceOpen || expanded

  const handleClick = () => {
    if (!forceOpen) {
      onToggleExpanded()
    }
  }

  return (
    <div>
      <CategoryItem
        Icon={category.icon}
        title={category.title}
        id={generateIdForMenuItem(category.title)}
        handleClick={handleClick}
        ArrowIconNode={<FoldArrows open={isOpen} handleClick={handleClick} />}
        warningIcon={category.warningIcon}
        chip={category.chip}
        highlightText={highlightText}
      />
      {isOpen && (
        <ListItems
          category={category}
          onMenuTap={onMenuTap}
          accessRightsSetting={accessRightsSetting}
          forceOpen={forceOpen}
          highlightText={highlightText}
        />
      )}
    </div>
  )
}
const NestedList = ({ category, onMenuTap, accessRightsSetting, chip, forceOpen, highlightText }) => {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(() => getDefaultExpanded({ config: category, pathname }))
  const isOpen = forceOpen || open

  const handleNestedListToggle = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!forceOpen) {
      setOpen((open) => !open)
    }
  }

  return (
    <div>
      <MenuItemLink
        title={category.title}
        url={category.url}
        id={generateIdForMenuItem(category.title)}
        open={!!isOpen}
        onClick={!category.url ? handleNestedListToggle : undefined}
        ArrowIconNode={<FoldArrows open={isOpen} handleClick={handleNestedListToggle} />}
        warningIcon={category.warningIcon}
        chip={chip}
        highlightText={highlightText}
      />
      {isOpen && (
        <div style={{ paddingLeft: 30 }}>
          <ListItems
            category={category}
            onMenuTap={onMenuTap}
            accessRightsSetting={accessRightsSetting}
            forceOpen={forceOpen}
            highlightText={highlightText}
          />
        </div>
      )}
    </div>
  )
}

const ListItems = ({ category, onMenuTap, accessRightsSetting, forceOpen, highlightText }) => {
  return (
    <>
      {(category.cards || []).map((childComponent) => {
        if (!childComponent.shouldShow) return null

        return childComponent.cards ? (
          <UiContainer uiKey={childComponent?.uiKey} key={childComponent.title}>
            <NestedList
              key={childComponent.title}
              onMenuTap={onMenuTap}
              category={childComponent}
              accessRightsSetting={accessRightsSetting}
              chip={childComponent.chip}
              forceOpen={forceOpen}
              highlightText={highlightText}
            />
          </UiContainer>
        ) : (
          <UiContainer uiKey={childComponent?.uiKey} key={childComponent.title}>
            <MenuItemLink
              key={childComponent.title}
              id={generateIdForMenuItem(childComponent.title)}
              title={childComponent.title}
              url={childComponent.url}
              warningIcon={childComponent.warningIcon}
              chip={childComponent.chip}
              externalLink={childComponent.externalLink}
              highlightText={highlightText}
            />
          </UiContainer>
        )
      })}
    </>
  )
}

const uiKey = 'control.'

const ControlMenu = ({ onMenuTap = () => null }) => {
  const { pathname } = useLocation()
  const isOsLite = useSelector(viewModeSelectors.isUserLite)
  const accessRightsSetting = useSelector(permissionsSelectors.getPermissionsSetting)
  const menuData = useMenuData(uiKey)
  const translate = useTranslate()
  const [searchText, setSearchText] = useState('')
  const [expandedCategories, setExpandedCategories] = useState(() => new Set())
  const searchTerm = searchText.trim().toLowerCase()
  const isSearching = !!searchTerm
  const visibleMenuData = useMemo(() => menuData.filter((category) => category.shouldShow), [menuData])
  const filteredMenuData = useMemo(
    () => (isSearching ? filterVisibleMenuData(menuData, searchTerm, translate) : visibleMenuData),
    [isSearching, menuData, searchTerm, translate, visibleMenuData]
  )

  const toggleCategoryExpanded = (title) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(title)) {
        next.delete(title)
      } else {
        next.add(title)
      }
      return next
    })
  }

  useEffect(() => {
    setExpandedCategories((prev) => {
      let next = prev
      visibleMenuData.forEach((category) => {
        if (getDefaultExpanded({ config: category, pathname }) && !next.has(category.title)) {
          if (next === prev) {
            next = new Set(prev)
          }
          next.add(category.title)
        }
      })
      return next
    })
  }, [pathname, visibleMenuData])

  useEffect(() => {
    if (pathname === '/control' && isOsLite) {
      setExpandedCategories((prev) => {
        if (prev.has('Company')) {
          return prev
        }
        const next = new Set(prev)
        next.add('Company')
        return next
      })
    }
  }, [pathname, isOsLite])

  const searchLabel = translate('Search Control')
  const clearSearchLabel = translate('Clear control navigation search')
  const noResultsLabel = translate('No control navigation sections found')

  return (
    <UiContainer uiKey={uiKey} style={styles.main}>
      <ComponentVersionsInherit versions={ComponentVersions_3_0}>
        {
          <div>
            <SearchRow>
              <SearchWrapper>
                <BasicInput
                  fullWidth
                  value={searchText}
                  placeholder={searchLabel}
                  onChange={setSearchText}
                  inputProps={{ 'aria-label': searchLabel }}
                  InputProps={{
                    startAdornment: <SearchIcon />,
                    endAdornment: searchText ? (
                      <ClearSearchButton aria-label={clearSearchLabel} onClick={() => setSearchText('')}>
                        <ClearOutlinedIcon fontSize="small" />
                      </ClearSearchButton>
                    ) : undefined,
                  }}
                  variant="outlined"
                />
              </SearchWrapper>
            </SearchRow>
            {isSearching && filteredMenuData.length === 0 && (
              <NoResults>
                <Typography textVariant="body2" color="textSecondary">
                  {noResultsLabel}
                </Typography>
              </NoResults>
            )}
            {filteredMenuData.map((category, categoryIndex) => {
              if (!category.shouldShow) return null
              if (category.isDivider) {
                return <Divider key={'divider-' + categoryIndex} style={{ margin: '8px 16px' }} />
              }
              return (
                <UiContainer uiKey={category?.uiKey} key={category.title}>
                  <MenuList
                    key={category.title}
                    category={category}
                    onMenuTap={onMenuTap}
                    accessRightsSetting={accessRightsSetting}
                    forceOpen={isSearching}
                    expanded={expandedCategories.has(category.title)}
                    onToggleExpanded={() => toggleCategoryExpanded(category.title)}
                    highlightText={searchText}
                  />
                </UiContainer>
              )
            })}
          </div>
        }
      </ComponentVersionsInherit>
    </UiContainer>
  )
}

const mapStateToProps = (state) => ({})

export default connect(mapStateToProps, { updateQuickStartProcess: setQuickStartAction })(ControlMenu)
